import React, { useEffect, useRef, useState } from 'react'

// import debounce from 'lodash/debounce'
import get from 'lodash/get'
import head from 'lodash/head'
import PT from 'prop-types'
import ReactMapGL, { NavigationControl } from 'react-map-gl'
import { Editor, EditorModes, RenderStates } from 'react-map-gl-draw'

// import usePrevious from '../../hooks/usePrevious'
// import sendFullStoryEvent from '../../utils/analytics/sendFullStoryEvent'
// import fullstory from '../../utils/constants/fullstory'
import Colors from '../hocs/colors/index'

const zoomThreshold = 4

const statesLayer = {
  filter: ['==', 'isState', true],
  id: 'state-population',
  maxzoom: zoomThreshold,
  paint: {
    'fill-color': [
      'interpolate',
      ['linear'],
      ['get', 'population'],
      0,
      Colors.mapBlue1,
      500000,
      Colors.mapBlue2,
      750000,
      Colors.mapBlue3,
      1000000,
      Colors.mapBlue4,
      2500000,
      Colors.mapBlue5,
      5000000,
      Colors.mapBlue6,
      7500000,
      Colors.mapBlue7,
      10000000,
      Colors.mapBlue8,
      25000000,
      Colors.mapBlue9,
    ],
    'fill-opacity': 0.6,
  },
  source: 'population',
  'source-layer': 'state_county_population_2014_cen',
  type: 'fill',
}

const countiesLayer = {
  filter: ['==', 'isCounty', true],
  id: 'county-population',
  minzoom: zoomThreshold,
  paint: {
    'fill-color': [
      'interpolate',
      ['linear'],
      ['get', 'population'],
      0,
      Colors.mapBlue1,
      10000,
      Colors.mapBlue2,
      25000,
      Colors.mapBlue3,
      50000,
      Colors.mapBlue4,
      100000,
      Colors.mapBlue5,
      250000,
      Colors.mapBlue6,
      500000,
      Colors.mapBlue7,
      750000,
      Colors.mapBlue8,
      1500000,
      Colors.mapBlue9,
    ],
    'fill-opacity': 0.6,
  },
  source: 'population',
  'source-layer': 'state_county_population_2014_cen',
  type: 'fill',
}

// const reportZoomEvent = debounce((prevZoom, viewport) => {
//   // const zoomStr =
//   //   prevZoom < viewport.zoom ? 'zoom in' : prevZoom > viewport.zoom ? 'zoom out' : null
//   // const eventProperties = {
//   //   latitude_real: viewport.latitude,
//   //   longitude_real: viewport.longitude,
//   //   zoom_str: zoomStr,
//   // }
//   // sendFullStoryEvent(fullstory.eventNames.mapZoom, eventProperties)
// }, 1000)

const KEYBOARD_HINT_STYLES = /** @type {React.CSSProperties} */ ({
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: 1,
  margin: -1,
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: 1,
})

const HeatMapChart = ({
  data,
  handleSelection,
  height,
  latitude,
  longitude,
  zoom,
  filtersApplied,
  mapSelectionEnabled,
}) => {
  const [viewport, updateViewport] = useState({
    height: height ? height : 300,
    latitude: latitude ? latitude : 39.8283,
    longitude: longitude ? longitude : -98.5795,
    maxZoom: 5,
    minZoom: 2.6,
    pitch: 40,
    width: '100%',
    zoom: 3,
  })
  const [selectedDrawMode, setSelectedDrawMode] = useState(EditorModes.READ_ONLY)

  useEffect(
    () =>
      updateViewport({
        height: height ? height : 300,
        latitude: latitude ? latitude : 39.8283,
        longitude: longitude ? longitude : -98.5795,
        maxZoom: 5,
        minZoom: 2.6,
        pitch: 40,
        width: '100%',
        zoom: zoom ? zoom : 3,
      }), // eslint-disable-next-line
    [latitude, longitude, zoom],
  )

  // const prevZoom = usePrevious(viewport.zoom)

  const handleViewportChange = newViewport => {
    // if (prevZoom && prevZoom !== viewport.zoom) {
    //   reportZoomEvent(prevZoom, viewport)
    // }
    updateViewport(prev => ({
      ...newViewport,
      maxZoom: prev.maxZoom,
      minZoom: prev.minZoom,
      zoom: Math.min(Math.max(newViewport.zoom, prev.minZoom), prev.maxZoom),
    }))
  }

  const handleKeyDown = e => {
    const PAN_STEP = 4
    const ZOOM_STEP = 0.5
    let handled = false

    switch (e.key) {
      case '+':
      case '=':
        updateViewport(prev => ({
          ...prev,
          zoom: Math.min(prev.zoom + ZOOM_STEP, prev.maxZoom || 5),
        }))
        handled = true
        break
      case '-':
        updateViewport(prev => ({
          ...prev,
          zoom: Math.max(prev.zoom - ZOOM_STEP, prev.minZoom || 2.6),
        }))
        handled = true
        break
      case 'ArrowUp':
        updateViewport(prev => ({ ...prev, latitude: prev.latitude + PAN_STEP }))
        handled = true
        break
      case 'ArrowDown':
        updateViewport(prev => ({ ...prev, latitude: prev.latitude - PAN_STEP }))
        handled = true
        break
      case 'ArrowLeft':
        updateViewport(prev => ({ ...prev, longitude: prev.longitude - PAN_STEP }))
        handled = true
        break
      case 'ArrowRight':
        updateViewport(prev => ({ ...prev, longitude: prev.longitude + PAN_STEP }))
        handled = true
        break
      default:
        break
    }

    if (handled) {
      e.preventDefault()
    }
  }

  const [cursorStyle, setCursorStyle] = useState('grab')

  const heatMapRef = useRef(null)

  const maxCount = get(head(get(data, 'features')), 'properties.maxCount')

  const getEditHandleStyle = ({ state }) => {
    switch (state) {
      case RenderStates.SELECTED:
      case RenderStates.HOVERED:
      case RenderStates.UNCOMMITTED:
        return {
          fill: 'rgb(60, 178, 208)',
          fillOpacity: 1,
          r: 7,
          stroke: 'rgb(60, 178, 208)',
          strokeWidth: 2,
        }

      default:
        return {
          fill: 'rgb(60, 178, 208)',
          fillOpacity: 1,
          r: 5,
          stroke: 'rgb(60, 178, 208)',
          strokeWidth: 2,
        }
    }
  }

  const getFeatureStyle = ({ state }) => {
    switch (state) {
      case RenderStates.SELECTED:
      case RenderStates.HOVERED:
      case RenderStates.UNCOMMITTED:
      case RenderStates.CLOSING:
        return {
          fill: 'rgb(60, 178, 208)',
          fillOpacity: 0.3,
          stroke: 'rgb(60, 178, 208)',
          strokeDasharray: '4,2',
          strokeWidth: 2,
        }

      default:
        return {
          fill: 'rgb(60, 178, 208)',
          fillOpacity: 0.1,
          stroke: 'rgb(60, 178, 208)',
          strokeWidth: 2,
        }
    }
  }

  const onDelete = () => handleSelection([])

  const onUpdate = options => {
    if (options.editType === 'addFeature') {
      setSelectedDrawMode(EditorModes.EDITING)
      handleSelection(head(get(head(get(options, 'data', [])), 'geometry.coordinates')))
    }
  }

  const renderToolbar = () => {
    return (
      <div className="mapboxgl-ctrl-group mapboxgl-ctrl">
        <button
          aria-label="Select area"
          className="mapbox-gl-draw_ctrl-draw-btn mapbox-gl-draw_polygon"
          title="Select Area"
          onClick={() => {
            setSelectedDrawMode(EditorModes.DRAW_RECTANGLE)
            setCursorStyle('crosshair')
          }}
        />
        <button
          aria-label="Delete selection"
          style={filtersApplied ? null : { cursor: 'not-allowed', opacity: 0.5 }}
          className="mapbox-gl-draw_ctrl-draw-btn mapbox-gl-draw_trash"
          title="Delete"
          onClick={filtersApplied ? () => onDelete() : null}
        />
      </div>
    )
  }

  const handleMapLoaded = () => {
    const map = heatMapRef.current.getMap()

    map.addSource('population', {
      type: 'vector',
      url: 'mapbox://mapbox.660ui7x6',
    })

    map.addLayer(statesLayer, 'waterway-label')

    map.addLayer(countiesLayer, 'waterway-label')

    map.addSource('peoplecount', {
      cluster: true,
      // Radius of each cluster when clustering points (defaults to 50)
      clusterProperties: {
        sum: ['+', ['get', 'count']],
      },

      clusterRadius: 60,

      data,
      type: 'geojson',
    })

    map.addLayer({
      filter: ['has', 'point_count'],
      id: 'clusters',
      paint: {
        'circle-color': [
          'step',
          ['get', 'sum'],
          Colors.mapBubble1,
          maxCount / 10,
          Colors.mapBubble2,
          maxCount / 3,
          Colors.mapBubble3,
          maxCount,
          Colors.mapBubble4,
        ],
        'circle-radius': [
          'step',
          ['get', 'sum'],
          15,
          maxCount / 10,
          20,
          maxCount / 3,
          25,
          maxCount,
          32,
        ],
      },
      source: 'peoplecount',
      type: 'circle',
    })

    map.addLayer({
      filter: ['has', 'sum'],
      id: 'cluster-count',
      layout: {
        'text-field': ['number-format', ['get', 'sum'], { locale: 'us', style: 'currency' }],
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12,
      },
      source: 'peoplecount',
      type: 'symbol',
    })

    map.addLayer({
      filter: ['!=', 'cluster', true],
      id: 'unclustered-point',
      paint: {
        'circle-color': '#fff',
        'circle-radius': ['step', ['get', 'count'], 5, 9, 10, 99, 20, 999, 40, 9999, 60],
        'circle-stroke-color': '#fff',
        'circle-stroke-width': 1,
      },
      source: 'peoplecount',
      type: 'circle',
    })

    map.addLayer({
      filter: ['!=', 'cluster', true],
      id: 'unclustered-count',
      layout: {
        'text-field': [
          'number-format',
          ['get', 'count'],
          { 'max-fraction-digits': 0, 'min-fraction-digits': 0 },
        ],
        'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
        'text-size': 14,
      },
      paint: {
        'text-color': '#fc4e2a',
      },
      source: 'peoplecount',
      type: 'symbol',
    })
  }

  return (
    <div
      aria-describedby="heatmap-keyboard-hint"
      className="heatmap-keyboard-focusable"
      onKeyDown={handleKeyDown}
      role="application"
      tabIndex={0}
    >
      <style>
        {
          '.heatmap-keyboard-focusable:focus-visible { outline: 2px solid #1890ff; outline-offset: 2px; }'
        }
      </style>
      <span id="heatmap-keyboard-hint" style={KEYBOARD_HINT_STYLES}>
        Interactive map. Use plus and minus keys to zoom in and out. Use arrow keys to pan. Mouse
        users can drag to pan and scroll to zoom.
      </span>
      <ReactMapGL
        {...viewport}
        onLoad={handleMapLoaded}
        onViewportChange={handleViewportChange}
        mapStyle="mapbox://styles/mapbox/light-v10"
        mapboxApiAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
        ref={heatMapRef}
      >
        <div style={{ left: 10, position: 'absolute', top: 10 }}>
          <NavigationControl showCompass={false} onViewportChange={handleViewportChange} />
        </div>
        {mapSelectionEnabled && (
          <div style={{ left: 10, position: 'absolute', top: 74 }}>{renderToolbar()}</div>
        )}
        <Editor
          clickRadius={5}
          editHandleShape="circle"
          mode={selectedDrawMode}
          onUpdate={onUpdate}
          featureStyle={getFeatureStyle}
          editHandleStyle={getEditHandleStyle}
          style={{ cursor: cursorStyle }}
        />
      </ReactMapGL>
    </div>
  )
}

HeatMapChart.propTypes = {
  className: PT.string,
  data: PT.object,
  defaultZoom: PT.number,
  filtersApplied: PT.bool,
  handleSelection: PT.func,
  height: PT.number.isRequired,
  latitude: PT.number,
  longitude: PT.number,
  mapSelectionEnabled: PT.bool.isRequired,
  zoom: PT.number,
}

HeatMapChart.defaultProps = {
  height: 600,
}

export default HeatMapChart
